import { datadogRum } from '@datadog/browser-rum';
import type { LDClient, LDContext, LDOptions } from 'launchdarkly-js-client-sdk';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import kebabCase from 'lodash/kebabCase';
import { defineStore } from 'pinia';
import { computed, type Ref, ref, watch } from 'vue';

import { env } from '@/env';
import { useIdentityStore } from '@/stores/identity';

export type FlagValues = {
  /**
   * Temporary local override used across the app.
   */
  internalPoc: boolean;
  /**
   * Most flags in Dash are boolean LD variations; treat unknown keys as boolean/undefined.
   */
  [key: string]: boolean | undefined;
};

export const createFlagsProxy = (base: Partial<FlagValues> = {}): FlagValues =>
  new Proxy(
    {
      internalPoc: false,
      ...base,
    } as FlagValues,
    {
      get(target, prop, receiver) {
        // Vue uses these to determine if this is a ref; we must return the real values.
        if (prop === '__v_raw' || prop === '__v_isRef') {
          return Reflect.get(target, prop, receiver);
        }

        const value = Reflect.get(target, prop, receiver) as boolean | undefined;
        return value === undefined ? true : value;
      },
    },
  );

export const useFlagStore = defineStore('flag', () => {
  const clientSideId = env.launchdarklyToken;
  const identityStore = useIdentityStore();

  let waitForUserFlagsResolve: () => void;
  let waitForUserFlagsReject: (reason?: unknown) => void;
  const waitForUserFlags = new Promise<void>((resolve, reject) => {
    waitForUserFlagsResolve = resolve;
    waitForUserFlagsReject = reject;
  });

  const initialized = ref(false);
  const ready = ref(false);
  const userFlagsReady = ref(false);
  const internalFlags = ref({} as FlagValues);
  const clientError: Ref<unknown> = ref(null);
  const isMocked = ref(false);
  const flagsMock = ref<FlagValues | null>(null);
  let client: LDClient | null = null;

  function createVariationFlagsProxy(): FlagValues {
    return new Proxy({} as FlagValues, {
      get(target, prop, receiver) {
        // Vue uses this to determine if this is a ref; we must return the real value.
        if (prop === '__v_isRef') {
          return Reflect.get(target, prop, receiver);
        }
        if (typeof prop !== 'string') {
          return Reflect.get(target, prop, receiver);
        }

        const flagKey = kebabCase(prop);
        // To temporarily set a flag for local development, add it here as follows:
        // return flagKey === 'name-of-my-flag' ? true : client?.variation(flagKey);
        return client?.variation(flagKey) as boolean | undefined;
      },
    });
  }

  const flags = computed<FlagValues>({
    get() {
      return isMocked.value ? (flagsMock.value as FlagValues) : internalFlags.value;
    },
    set(v) {
      if (isMocked.value) {
        flagsMock.value = v;
      } else {
        internalFlags.value = v;
      }
    },
  });

  function ldUser(): LDContext {
    const identity = identityStore.identity as {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
      organization?: { label: string };
    } | null;
    if (identity) {
      return {
        kind: 'user',
        key: identity.id,
        email: identity.email,
        name: `${identity.first_name} ${identity.last_name}`,
        brand: identityStore.currentBrand?.label,
        organization: identity.organization?.label,
      };
    }
    return {
      kind: 'user',
      key: 'anonymous',
      email: 'test+anonuser@dashhudson.com',
      name: 'Anonymous User',
    };
  }

  function mockFlags(obj: Partial<FlagValues> = {}) {
    ready.value = true;
    waitForUserFlagsResolve();
    userFlagsReady.value = true;
    flagsMock.value = createFlagsProxy(obj);
    isMocked.value = true;
  }
  function clearFlagsMock() {
    flagsMock.value = null;
    isMocked.value = false;
  }

  function createLdClient() {
    if (!client) {
      clearFlagsMock();

      const context = ldUser();
      const ldOptions: LDOptions = {
        fetchGoals: false,
        bootstrap: 'localStorage',
        inspectors: [
          {
            type: 'flag-used',
            name: 'dd-inspector',
            method: (key, detail) => {
              datadogRum.addFeatureFlagEvaluation(key, detail.value);
            },
          },
        ],
      };

      if (env.launchDarklyRelay) {
        ldOptions.streamUrl = env.launchDarklyRelay;
        ldOptions.baseUrl = env.launchDarklyRelay;
        ldOptions.eventsUrl = env.launchDarklyRelay;
      }

      client = LaunchDarkly.initialize(clientSideId, context, ldOptions);
      client.on('ready', () => {
        internalFlags.value = createVariationFlagsProxy();
        ready.value = true;
      });
      client.on('change', () => {
        internalFlags.value = createVariationFlagsProxy();
      });
      client.on('error', (e) => {
        clientError.value = e;
        waitForUserFlagsReject(e);
      });
    }
  }

  function updateLdIdentity() {
    if (client) {
      userFlagsReady.value = false;
      void client.identify(ldUser(), undefined, () => {
        const ctx = client?.getContext();
        if (ctx && 'kind' in ctx && ctx.kind === 'user' && ctx.key !== 'anonymous') {
          userFlagsReady.value = true;
          waitForUserFlagsResolve();
        }
      });
    }
  }

  watch(
    () => identityStore.identity,
    () => {
      createLdClient();
    },
    {
      immediate: true,
    },
  );

  watch(
    () => identityStore.currentBrand,
    () => {
      updateLdIdentity();
    },
  );

  function updateFlags({
    ready: incomingReady = false,
    flags: incomingFlags = {} as FlagValues,
  }: {
    ready?: boolean;
    flags?: FlagValues;
  } = {}) {
    ready.value = incomingReady;
    if (incomingReady) {
      initialized.value = true;
      internalFlags.value = incomingFlags;
    }
  }

  return {
    initialized,
    ready,
    userFlagsReady,
    flags,
    clientError,
    updateFlags,
    waitForUserFlags,
    mockFlags,
    isMocked,
    clearFlagsMock,
  };
});
